import React, { useState, useEffect } from 'react';
import { History, Users, Loader2, Search, Wallet, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';
import { useSocket } from '../context/SocketContext';

interface DriverSummary {
  id: string;
  name: string;
  balance: number;
  phone: string;
  email?: string;
  bankDetails: any;
  pendingRequest: {
    id: string;
    amount: number;
    status: string;
    requestedAt: string;
  } | null;
}

const SettledDrivers = () => {
  const [drivers, setDrivers] = useState<DriverSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [expandedDriverId, setExpandedDriverId] = useState<string | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const { socket } = useSocket();

  const fetchData = async () => {
    try {
      const response = await api.get('/drivers/payout-summary');
      // Show ALL verified drivers
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleHistory = async (driverId: string) => {
    if (expandedDriverId === driverId) {
      setExpandedDriverId(null);
      setHistory([]);
    } else {
      setExpandedDriverId(driverId);
      setLoadingHistory(true);
      try {
        const response = await api.get(`/driver/${driverId}/payouts`);
        setHistory(response.data);
      } catch (error) {
        console.error('Error fetching history:', error);
      } finally {
        setLoadingHistory(false);
      }
    }
  };

  useEffect(() => {
    fetchData();

    if (socket) {
      socket.on('driver_updated', fetchData);
      socket.on('driver_approved', fetchData);
      socket.on('payout_processed', fetchData);

      return () => {
        socket.off('driver_updated', fetchData);
        socket.off('driver_approved', fetchData);
        socket.off('payout_processed', fetchData);
      };
    }
  }, [socket]);

  const filteredDrivers = drivers.filter(d => {
    const searchLower = search.toLowerCase().trim();
    const nameMatch = (d.name || '').toLowerCase().includes(searchLower);
    const phoneMatch = (d.phone || '').includes(searchLower);
    return nameMatch || phoneMatch;
  });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-[#05A664] animate-spin mb-4" />
        <p className="text-[#909090] font-medium">Loading settled accounts...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-extrabold text-[#121415] tracking-tight">Payment History</h1>
          <p className="text-[#909090] font-medium">Audit and review all settled driver balances and transactions.</p>
        </div>
        
        <div className="relative group">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5 group-focus-within:text-[#05A664] transition-colors" />
          <input 
            type="text"
            placeholder="Search driver name or phone..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 pr-6 py-4 bg-white border border-gray-100 rounded-2xl w-full md:w-80 shadow-sm focus:ring-2 focus:ring-[#05A664]/10 focus:border-[#05A664] outline-none transition-all font-medium text-sm"
          />
        </div>
      </div>

      {/* Grid */}
      {filteredDrivers.length === 0 ? (
        <div className="bg-white rounded-[2rem] p-20 flex flex-col items-center justify-center border border-dashed border-gray-200">
          <div className="w-20 h-20 bg-gray-50 rounded-3xl flex items-center justify-center mb-4">
            <Search className="w-10 h-10 text-gray-300" />
          </div>
          <h3 className="text-xl font-bold text-[#121415]">No Drivers Found</h3>
          <p className="text-[#909090] font-medium max-w-xs text-center">We couldn't find any drivers matching "{search}". Try searching by a different name or phone number.</p>
          <button 
            onClick={() => setSearch('')}
            className="mt-6 text-[#05A664] font-bold text-sm hover:underline"
          >
            Clear Search
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDrivers.map((driver) => (
          <div key={driver.id} className="bg-white border border-gray-100 rounded-[2rem] p-8 hover:border-[#05A664]/30 transition-all group shadow-sm flex flex-col h-fit">
            <div className="flex items-center justify-between mb-8">
              <div className="w-14 h-14 bg-[#F1F8F5] rounded-2xl flex items-center justify-center">
                <Users className="w-7 h-7 text-[#05A664]" />
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-[#909090] uppercase mb-1 tracking-widest">Current Balance</p>
                <div className="flex items-center gap-1 justify-end">
                  <Wallet className="w-4 h-4 text-[#05A664]" />
                  <p className="text-2xl font-black text-[#121415]">Rs {driver.balance.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-xl font-bold text-[#121415]">{driver.name}</h4>
                {driver.pendingRequest && (
                  <span className="px-2 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold uppercase rounded-md">Pending Payout</span>
                )}
              </div>
              <p className="text-sm text-[#909090] font-medium">{driver.phone}</p>
              <p className="text-xs text-[#909090] font-medium mt-0.5">{driver.email || 'No email'}</p>
            </div>
            
            <button
              onClick={() => toggleHistory(driver.id)}
              className={`w-full py-4 rounded-2xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                expandedDriverId === driver.id 
                ? 'bg-[#121415] text-white shadow-xl shadow-black/20' 
                : 'bg-gray-50 text-[#909090] hover:bg-gray-100'
              }`}
            >
              <History className="w-5 h-5" />
              {expandedDriverId === driver.id ? 'Hide History' : 'Payout History'}
              {expandedDriverId === driver.id ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
            </button>

            {expandedDriverId === driver.id && (
              <div className="mt-8 pt-8 border-t border-gray-50 space-y-4 animate-in slide-in-from-top-4 duration-300">
                {loadingHistory ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#05A664]" /></div>
                ) : history.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50/50 rounded-2xl">
                    <p className="text-xs text-[#909090] font-bold uppercase tracking-widest">No Past Requests</p>
                  </div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-3">
                    {history.map(item => (
                      <div key={item.id} className="p-5 bg-gray-50/80 rounded-2xl border border-gray-50 space-y-4">
                        <div className="flex justify-between items-center">
                          <span className={`text-[10px] font-black px-2 py-1 rounded-md uppercase tracking-wider ${
                            item.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
                          }`}>{item.status}</span>
                          <p className="text-xl font-black text-[#121415]">Rs {item.amount.toLocaleString()}</p>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200/50">
                          <div className="flex flex-col">
                            <span className="text-[9px] font-bold text-[#909090] uppercase tracking-widest mb-1">Requested On</span>
                            <span className="text-[11px] font-bold text-[#121415]">{new Date(item.requestedAt).toLocaleDateString()}</span>
                            <span className="text-[10px] text-[#909090]">{new Date(item.requestedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          
                          {item.status === 'approved' && (
                            <div className="flex flex-col text-right">
                              <span className="text-[9px] font-bold text-[#909090] uppercase tracking-widest mb-1">Settled On</span>
                              <span className="text-[11px] font-bold text-[#121415]">{new Date(item.paidAt).toLocaleDateString()}</span>
                              <span className="text-[10px] text-[#909090]">{new Date(item.paidAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )}
    </div>
  );
};

export default SettledDrivers;
